import { Component, ComponentInterface, Element, h, Host, Prop, Watch } from '@stencil/core';

interface UiCmsContainerBody {
  backgroundStyle:
    | 'neutral-100'
    | 'primary-100'
    | 'primary'
    | 'border-neutral-400'
    | 'white'
    | 'border-primary'
    | 'border-white';
  isFullWidth?: boolean;
  padding: 'default' | 'sm' | 'none';
  brandshape?: boolean;
}

@Component({
  tag: 'ui-cms-container',
  styleUrl: 'ui-cms-container.scss',
})
export class UiCmsContainer implements ComponentInterface {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsContainerBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  /**
   * needed in some specific cases when a cms-container is used inside a cms-container
   * i.e. parent cms-container has a primary background color and the child cms-container has a transparent brandshape
   */
  @Prop() inverted: boolean;

  private refBrandshapeContentWrapper: HTMLElement;

  // Legacy code, can be removed as soon as all cms components are migrated to SD
  private setVariantWhiteFor(componentName: string): void {
    const componentsToBeWhite = [].slice.call(this.el.querySelectorAll(componentName));

    if (componentsToBeWhite.length > 0 && componentName === 'ui-cms-headline') {
      componentsToBeWhite.forEach((component) => component.setAttribute('icon-variant', 'white'));
    } else if (componentsToBeWhite.length > 0) {
      componentsToBeWhite.forEach((component) => component.setAttribute('variant', 'white'));
    }
  }

  private setInvertedAttributesAndClasses(element: HTMLElement): void {
    const sdClass = this.checkIfElemHasSdClass(element);
    if (sdClass) {
      element.classList.add(`${sdClass}--inverted`);
    } else {
      element.setAttribute('inverted', 'true');
    }
  }

  /**
   * sets the inverted property on every child of the ui-cms-container.
   * This is important for the skinning of the sds components.
   */
  private setInvertedState(brandshape?: boolean): void {
    if (!brandshape) {
      Array.from(this.el.children).forEach((element: HTMLElement) => {
        this.setInvertedAttributesAndClasses(element);
      });
    } else {
      Array.from(this.refBrandshapeContentWrapper?.children).forEach((element: HTMLElement) => {
        this.setInvertedAttributesAndClasses(element);
      });
    }
  }

  private checkIfElemHasSdClass(element: HTMLElement): string {
    const classes = element.className.split(/\s+/);
    const sdClass = classes.find((cls) => cls.startsWith('sd-'));
    return sdClass;
  }

  private setVariantForBrandshape() {
    if (this.body.brandshape && this.body.backgroundStyle) {
      const brandshapeElement = this.el.querySelector('sd-brandshape');
      if (brandshapeElement) {
        brandshapeElement.setAttribute('variant', this.body.backgroundStyle);
      }
    }
  }

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
    if (this.body.backgroundStyle === 'primary') this.setInvertedState();
  }

  componentDidLoad() {
    if (this.body.backgroundStyle === 'primary') {
      this.setVariantWhiteFor('ui-cms-teaser-group');
      this.setVariantWhiteFor('ui-cms-teaser-group-tile');
      this.setVariantWhiteFor('ui-cms-table');
      this.setVariantWhiteFor('ui-cms-quickfact');
    }

    if (
      this.inverted &&
      this.body.brandshape &&
      ['border-primary', 'border-white', 'border-neutral-400'].includes(this.body.backgroundStyle)
    ) {
      this.setInvertedState(true);
    }
    this.setVariantForBrandshape();
  }

  render() {
    return this.body.brandshape ? (
      <Host class={{ brandshape: true, 'brandshape--variant-primary': this.body.backgroundStyle === 'primary' }}>
        <sd-brandshape>
          <div
            class="brandshape-content-wrapper"
            ref={(el) => {
              this.refBrandshapeContentWrapper = el;
            }}
          >
            <slot></slot>
          </div>
        </sd-brandshape>
      </Host>
    ) : (
      <Host
        class={{
          'sd-container': true,
          'full-width': this.body.isFullWidth,
          [`sd-container--variant-${this.body.backgroundStyle}`]: !!this.body.backgroundStyle,
          'no-padding': this.body.padding === 'none',
          [`sd-container--padding-${this.body.padding}`]: !!this.body.padding && this.body.padding !== 'none',
        }}
      >
        <slot></slot>
      </Host>
    );
  }
}
